import { approximatelyEquals } from '@tools/math/utility';
import { forwardRef, memo } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { getLoaderSkin, LoaderType } from './loader-skin';

const hideAnimation = keyframes`
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    display: none;
    pointer-events: none;
  }
`;

const Wrapper = memo(styled.div<{ color: string; $hide: boolean }>`
  position: absolute;
  overflow: hidden;
  width: 100%;
  height: 100%;
  z-index: 9999;
  ${({ color, $hide: hide }) => css`
    background-color: ${color};
    animation: ${hide ? hideAnimation : ''} 0.2s forwards;
    animation-delay: 0.4s;
  `}
`);

const Logo = memo(styled.div<{ url: string }>`
  ${({ url }) => css`
    position: relative;
    left: 50%;
    top: 45%;
    transform: translate(-50%, -50%);
    width: clamp(15rem, 80%, 35rem);
    height: 50%;
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;
    background-image: url(${url});
  `}
`);
const ProgressBarWrapper = styled.div<{ color?: string }>`
  position: absolute;
  left: 50%;
  bottom: 15%;
  transform: translateX(-50%);
  width: 40%;
  height: 1.5rem;
  border: 0.15rem solid ${({ color }) => color ?? '#fff'};
  overflow: hidden;
`;

const ProgressBarFill = styled.div<{ progress: number; color?: string }>`
  margin: 0.1rem;
  height: 80%;
  width: ${({ progress }) => Math.max(0, Math.min(1, progress)) * 98}%;
  background: ${({ color }) => color ?? '#fff'};
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

type Props = {
  loaderType: LoaderType;
  progress: number;
};

export const LoaderScreen = forwardRef<HTMLDivElement, Props>(({ loaderType, progress }, ref) => {
  const skin = getLoaderSkin(loaderType);
  return (
    <Wrapper color={skin.backgroundColor} $hide={approximatelyEquals(progress, 1)} ref={ref}>
      <Logo url={skin.logo} />
      <ProgressBarWrapper color={skin.progressBarColor}>
        <ProgressBarFill progress={progress} color={skin.progressBarColor} />
      </ProgressBarWrapper>
    </Wrapper>
  );
});
